import { HttpClient, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { Endpoint } from "../support/endpoint.support";
import { Enum } from "../support/enum.support";
import { EnvironmentService } from "./environment.service";
import { HTTPMethod } from "../model/http-method.enum";
import { Logger } from "../model/logger.model";
import { LoggingService } from "./logging.service";
import { HTTPRequest } from "../model/http.request.model";
import { UUID } from "../support/uuid.support";
import { GigotErrorResponse } from "../model/error/gigot-error-response.error";

	/**
	 * @description
	 *  Servicio que provee funcionalidad básica para comunicarse a través del
	 *  protocolo HTTP.
	 */

@Injectable({ providedIn: "root" })
export class HTTPService {

	private logger: Logger = this.loggingService.getLogger("http.service");

	constructor(
		private environmentService: EnvironmentService,
		private httpClient: HttpClient,
		private loggingService: LoggingService) {}

	/**
	 * @description
	 *  Construye el endpoint hacia un controlador en el servidor principal de
	 *  backend especificado en la configuración de entorno.
	 *
	 * @param controllerPath
	 *  Subpath hacia la base del controlador.
	 * @param relativePath
	 *  Subpath relativo dentro del controlador objetivo. Se eliminarán todos
	 *  los caracteres '/' al inicio de la cadena.
	 *
	 * @returns
	 *  El endpoint absoluto al cual emitir los requests.
	 */
	private endpoint(controllerPath: string, relativePath: string, host : string): URL {
		return Endpoint.join([
			host,
			controllerPath,
			relativePath
		]);
	}

	/**
	 * @description
	 *  Método genérico para realizar un request HTTP y realizar un pequeño
	 *  post-proceso sobre el response obtenido.
	 *
	 * @param method
	 *  Método HTTP que se desea ejecutar.
	 * @param controllerPath
	 *  Path relativo hacia el controlador actual que se desea ejecutar.
	 * @param relativePath
	 *  Path relativo dentro del controlador actual que se desea ejecutar.
	 * @param payload
	 *  Objeto opcional que debe enviarse como parte del request. Utilizado en
	 *  métodos como POST y PUT. Si no se especifica, no se envía ningún
	 *  payload.
	 * @param responseType
	 *  Tipo (opcional) esperado en el response. Por defecto, si no se
	 *  especifica ningún tipo, se espera un objeto JSON. Nótese que este no
	 *  es el tipo del modelo esperado, sino simplemente un tipo general que
	 *  el cliente HTTP requiere durante la serialización del mismo.
	 *
	 * @returns
	 *  Devuelve un Observable del objeto esperado, preparado para
	 *  subscripción.
	 */
	public request<T>(
			method: HTTPMethod, controllerPath: string, relativePath: string,
			payload: any = undefined, responseType: any = "json", host = this.environmentService.backendHost.href): Observable<T> {
		const endpoint: URL = this.endpoint(controllerPath, relativePath, host);
		const methodString: string = Enum.toString(HTTPMethod, method);
		const requestId = UUID.of128();
		this.logger.debug(`[${requestId}] ${methodString} ${endpoint.pathname}${endpoint.search}`)
		return this.httpClient
			.request<T>(methodString.toLowerCase(), endpoint.href, {
				body: payload,
				observe: "response",
				responseType: responseType
			})
			.pipe(
				map((response: HttpResponse<T>) => {
					this.logger.debug(`[${requestId}] ${methodString} ${endpoint.pathname}${endpoint.search} (${response.status}: ${response.statusText})`)
					return response.body;
				}),
				catchError((error: HttpErrorResponse) => {
					this.logger.error(`[${requestId}] ${methodString} ${endpoint.pathname}${endpoint.search}`, error);
					const maybeGigotException = new GigotErrorResponse(error);
					if (maybeGigotException.isGigotException) {
						return throwError(maybeGigotException);
					}
					else {
						return throwError(error);
					}
				})
			);
	}

	/**
	 * @description
	 *  Método conveniente para enviar un request HTTP.
	 *
	 * @template P
	 *  El tipo del payload enviado. Si no se envía ningún payload, debería
	 *  usar 'typeof undefined'.
	 * @template R
	 *  El tipo de la respuesta obtenida. Si no se espera una respuesta (como
	 *  por ejemplo al recibir un status '204: No Content'), debería usar el
	 *  tipo 'null'.
	 *
	 * @param request
	 *  Toda la información requerida para emitir el request.
	 *
	 * @returns
	 *  Devuelve el response de la consulta al igual que el método #request.
	 */
	public send<P, R>(request: HTTPRequest<P>): Observable<R> {
		return this.request<R>(
			request.method, request.controllerPath,
			request.relativePath, request.payload, request.responseType, request.host);
	}
}
